import { useEffect, useRef, useState } from 'react';
import useGameStore, { MONEY_TRANSFER_TTL_MS } from '../store/gameStore';
import type { MoneyTransfer, Player } from '../types/gameTypes';
import { money } from '../lib/format';
import { useT } from '../i18n';
import { BotIcon, FundIcon, LoanIcon, ShieldIcon, TrophyIcon, UserIcon } from './GameIcons';

/** Smoothly ticks a displayed number towards its target over `duration` ms (800 ms default). */
const useTickingNumber = (target: number, duration = 800) => {
  const shownRef = useRef(target);
  const [shown, setShown] = useState(target);
  useEffect(() => {
    const from = shownRef.current;
    if (from === target) return;
    const start = performance.now();
    let raf = 0;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const value = Math.round(from + (target - from) * eased);
      shownRef.current = value;
      setShown(value);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return shown;
};

/** Re-renders only while animations are alive, so idle screens do no work. */
const useTransferClock = (active: boolean) => {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 180);
    return () => window.clearInterval(id);
  }, [active]);
};

const LedgerCard = ({ player, isActive, transfers }: { player: Player; isActive: boolean; transfers: MoneyTransfer[] }) => {
  const t = useT();
  const netWorthOf = useGameStore((s) => s.netWorthOf);
  const investmentValueOf = useGameStore((s) => s.investmentValueOf);
  const loanExposureOf = useGameStore((s) => s.loanExposureOf);
  const setInspectedPlayer = useGameStore((s) => s.setInspectedPlayer);
  const cash = useTickingNumber(player.cash, 800);
  const investments = investmentValueOf(player.id);
  const loans = loanExposureOf(player.id);
  const paid = transfers.filter((t) => t.fromId === player.id).reduce((sum, t) => sum + t.amount, 0);
  const got = transfers.filter((t) => t.toId === player.id).reduce((sum, t) => sum + t.amount, 0);
  const receiving = got > 0;
  const paying = !receiving && paid > 0;
  return (
    <button
      type="button"
      onClick={() => setInspectedPlayer(player.id)}
      data-player-card={player.id}
      className={`relative w-full min-w-0 cursor-pointer rounded-lg border p-1.5 text-left transition-all duration-300 hover:border-amber-400/50 hover:bg-slate-800/80 ${
        receiving
          ? 'ledger-glow-get border-emerald-400/80 bg-emerald-500/10'
          : paying
            ? 'ledger-glow-pay border-rose-400/80 bg-rose-500/10'
            : isActive
              ? 'border-amber-500/70 bg-amber-500/10'
              : 'border-white/10 bg-black/20'
      } ${player.isBankrupt ? 'opacity-40 grayscale' : ''}`}
      title={`${player.name}${player.isAI ? ' (bot)' : ''} · ${t('ledger.netWorth')} ${money(netWorthOf(player.id))} · ${t('dossier.title')}`}
    >
      <div className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: player.color }} />
        <span className="min-w-0 flex-1 truncate text-[11px] font-bold text-slate-200">{player.name}</span>
        <span className="shrink-0 text-slate-400" title={player.isAI ? 'Bot' : 'Human'}>
          {player.isAI ? <BotIcon size={12} /> : <UserIcon size={12} />}
        </span>
        {isActive && !player.isBankrupt && <span className="shrink-0 text-[9px] font-black text-cyan-300">●</span>}
      </div>
      <p className={`mt-0.5 truncate font-mono text-sm font-black leading-none ${player.cash < 0 ? 'text-rose-300' : 'text-slate-50'}`}>
        {money(cash)}
      </p>
      <p className="mt-0.5 flex items-center gap-1 text-[9px] font-bold text-amber-200/80">
        <TrophyIcon size={10} /> {money(netWorthOf(player.id))}
      </p>
      {(receiving || paying) && (
        <span className={`cash-float pointer-events-none absolute -top-3 right-1 text-xs font-black ${receiving ? 'text-emerald-300' : 'text-rose-300'}`}>
          {receiving ? `+${money(got)}` : `−${money(paid)}`}
        </span>
      )}
      {player.debt > 0 && <p className="text-[9px] font-bold text-rose-300/90">{t('common.debt')} {money(player.debt)}</p>}
      {/* Treasury tracking: structured-loan servicing + fund positions stay visible. */}
      {loans > 0 && (
        <p className="flex items-center gap-1 text-[9px] font-bold text-rose-300/90" title={`${money(loans)}`}>
          <LoanIcon size={10} /> {t('common.loans')} {money(loans)}
        </p>
      )}
      {investments > 0 && (
        <p className="flex items-center gap-1 text-[9px] font-bold text-emerald-300/90" title={`${money(investments)}`}>
          <FundIcon size={10} /> {t('common.investments')} {money(investments)}
        </p>
      )}
      {player.insuranceTurnsRemaining > 0 && (
        <p className="flex items-center gap-1 text-[9px] font-bold text-sky-300/90" title={`${player.insuranceTurnsRemaining} ${t('common.rounds')}`}>
          <ShieldIcon size={10} /> {player.insurancePolicy ?? t('ledger.insurance')} · {player.insuranceTurnsRemaining} {t('common.rounds')}
        </p>
      )}
    </button>
  );
};

/**
 * Permanent player/bot roster shown in the centre of the board: live cash in large mono
 * type, red glow while paying, emerald glow (with a floating +$X) while receiving.
 * Loans, funds and insurance are printed for every seat — bots included — so the table
 * reads exactly the same for a human and an AI opponent.
 */
export const PlayerLedgerHud = () => {
  const t = useT();
  const players = useGameStore((s) => s.players);
  const currentPlayerIndex = useGameStore((s) => s.currentPlayerIndex);
  const moneyTransfers = useGameStore((s) => s.moneyTransfers);
  const openFinancialDesk = useGameStore((s) => s.openFinancialDesk);
  const fresh = moneyTransfers.filter((t) => Date.now() - t.ts < MONEY_TRANSFER_TTL_MS);
  useTransferClock(fresh.length > 0);
  const active = players[currentPlayerIndex];
  const financeBtn = 'flex min-h-[30px] flex-col items-center justify-center gap-0.5 rounded-lg border border-white/10 bg-white/5 px-1 py-1 text-[9px] font-bold text-slate-300 transition hover:border-amber-300/40 hover:bg-white/10 hover:text-amber-100';
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300/80">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-300" /> {t('board.liveLedger')}
      </p>
      <div className="grid grid-cols-3 gap-1">
        <button type="button" onClick={() => openFinancialDesk('LOANS')} className={financeBtn} title={t('common.loans')}>
          <LoanIcon size={13} /> {t('common.loans')}
        </button>
        <button type="button" onClick={() => openFinancialDesk('INVESTMENTS')} className={financeBtn} title={t('common.investments')}>
          <FundIcon size={13} /> {t('common.investments')}
        </button>
        <button type="button" onClick={() => openFinancialDesk('INSURANCE')} className={financeBtn} title={t('ledger.insurance')}>
          <ShieldIcon size={13} /> {t('ledger.insurance')}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {players.map((player) => (
          <div key={player.id} className="min-w-0">
            <LedgerCard
              player={player}
              isActive={player.id === active?.id}
              transfers={fresh.filter((t) => t.fromId === player.id || t.toId === player.id)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default PlayerLedgerHud;
